import flattenObject from '@atlas/tool/box/conditional-fields/flatten';
import valueComparator, { type TValue } from '@atlas/tool/box/conditional-fields/comparator';
import { allowAnyValue } from '@atlas/tool/box/conditional-fields/operators';

export enum CustomFieldCategory {
    ACCOUNT = 'account',
    CUSTOMER = 'customer',
    TICKET = 'ticket',
}

export function hasMatchingCondition({ rule, ticket, customer, account }: TMatchConditionsProps) {
    const entityToObject = {
        [CustomFieldCategory.TICKET]: ticket,
        [CustomFieldCategory.CUSTOMER]: customer,
        [CustomFieldCategory.ACCOUNT]: account,
    };

    const { conditions } = rule;

    // V1: keys of the conditions are always "all"
    const conditionsToEvaluate = conditions.all;
    let hasMatch = false;

    for (const condition of conditionsToEvaluate) {
        const { field, value, operator, entity } = condition;
        const flattenedEntity = flattenObject(entityToObject[entity]);

        const matchedFieldEntry = Object.keys(flattenedEntity).find(
            (key) => key === field || (field.startsWith('customFields.') && key === `${field}.key`),
        );
        if (!matchedFieldEntry) continue;

        const entityValueToCompare = flattenedEntity[matchedFieldEntry] as TValue;

        // now we have the value, we have to evaluate the condition
        // if the condition is true, then we return true, else we return false
        hasMatch = valueComparator(operator, value, entityValueToCompare);
        if (hasMatch) break;
    }

    return hasMatch;
}

export default function evaluate({ customField, ticket, customer, account }: TEvaluateProps) {
    const result: Partial<TCustomFieldRuleCondition> = {
        operator: undefined,
        value: undefined,
    };

    const { category, rules } = customField;
    if (
        (category === CustomFieldCategory.TICKET && !ticket) ||
        (category === CustomFieldCategory.CUSTOMER && !customer) ||
        (category === CustomFieldCategory.ACCOUNT && !account) ||
        !rules
    ) {
        return result;
    }

    for (const rule of rules) {
        const { actions } = rule;

        if (hasMatchingCondition({ rule, ticket, customer, account })) {
            // V1: we just have filter as the action and filter is always a list of one
            const [filter] = actions.filter;
            if (!filter) return result;
            if (filter.operator === allowAnyValue) return result;

            // Sample
            // since we are storing this directly in the custom field we can directly return the value
            // {
            //    field: #field,
            //    operator: "is one of",
            //    value: "[Office, Copilot, Cloud]",
            //    entity: #entity,
            // }
            result.operator = filter.operator;
            result.value = filter.value;
        }
    }

    return result;
}

export type TEvaluateProps = {
    customField: TConditionalCustomField;
    ticket?: Record<string, unknown>;
    customer?: Record<string, unknown>;
    account?: any;
};

export type TConditionalCustomField = {
    key: string;
    category: CustomFieldCategory;
    rules: TCustomFieldRule[];
    dependsOn: string[];
};

export type TCustomFieldRuleCondition = {
    field: string;
    operator: string;
    value: TValue;
    entity: CustomFieldCategory;
};

type TMatchConditionsProps = Omit<TEvaluateProps, 'customField'> & { rule: TCustomFieldRule };

export type TCustomFieldRule = {
    conditions: {
        all: TCustomFieldRuleCondition[];
    };
    actions: {
        filter: TCustomFieldRuleCondition[];
    };
};

// #region Tests

if (import.meta.vitest) {
    const { describe, it } = import.meta.vitest;
    describe('evaluate', async () => {
        it.todo('🤷🏼');
    });
}

// #endregion
